import React, { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { MEALPERIODS } from "../../../core/utils/mealTypes";
import { formatOptionTitle, optionNumber } from "../../../core/utils/formatOptionTitle";
import { extractOptionNumbersFromOptionIds } from "../../../core/diet-plan/mealDataByPeriod";

/**
 * Picker for copying a single recipe into other (meal type, option) slots on the
 * SAME plan or template — can cross meal types.
 * Only lists meal-type/option combos that already exist.
 */
export default function CopyFoodAcrossMealTypesModal({
  isOpen,
  onClose,
  foods = [],
  sourcePeriodName,
  sourceOptionTab,
  dataByPeriod = {},
  onConfirm,
}) {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedKeys([]);
    setIsSubmitting(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, isSubmitting]);

  const sourceOptionTitle = formatOptionTitle(sourceOptionTab);

  const groups = useMemo(() => {
    return MEALPERIODS.map((period) => {
      const optionIds = dataByPeriod?.[period]?.optionIds || {};
      const nums = extractOptionNumbersFromOptionIds(optionIds);
      const options = nums
        .map((num) => formatOptionTitle(num))
        .filter(
          (title) => !(period === sourcePeriodName && title === sourceOptionTitle)
        );
      return { period, options };
    }).filter((g) => g.options.length > 0);
  }, [dataByPeriod, sourcePeriodName, sourceOptionTitle]);

  if (!isOpen) return null;

  const allKeys = groups.flatMap((g) => g.options.map((title) => `${g.period}::${title}`));

  const toggle = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const getFoodName = (f) => f?.food_name || f?.food_details?.main_details?.food_name || "this recipe";
  const copyLabel =
    foods.length === 1
      ? getFoodName(foods[0])
      : `${foods.length} recipes`;

  const handleConfirm = async () => {
    if (!selectedKeys.length || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const targets = selectedKeys.map((key) => {
        const [periodName, optionTab] = key.split("::");
        return { periodName, optionTab };
      });
      await onConfirm(targets);
      onClose();
    } catch {
      // Parent shows error via toast; keep the modal open for retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4"
      onClick={() => !isSubmitting && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="copy-food-across-meal-types-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div>
            <h3 id="copy-food-across-meal-types-title" className="text-lg font-bold text-gray-900">
              Copy to another meal type
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Copy <span className="font-medium text-gray-700">{copyLabel}</span> from{" "}
              {sourcePeriodName}/{sourceOptionTitle} to the slots you select below.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {groups.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No other meal type/option slots available to copy to.
            </p>
          ) : (
            <>
              {allKeys.length > 1 && (
                <div className="flex gap-3 text-sm mb-2">
                  <button
                    type="button"
                    onClick={() => setSelectedKeys(allKeys)}
                    disabled={isSubmitting}
                    className="text-orange-primary hover:underline font-medium disabled:opacity-50"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedKeys([])}
                    disabled={isSubmitting}
                    className="text-gray-500 hover:underline disabled:opacity-50"
                  >
                    Clear
                  </button>
                </div>
              )}
              <div className="space-y-4">
                {groups.map((group) => (
                  <div key={group.period}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                      {group.period}
                    </p>
                    <ul className="space-y-1">
                      {group.options
                        .sort((a, b) => Number(optionNumber(a)) - Number(optionNumber(b)))
                        .map((title) => {
                          const key = `${group.period}::${title}`;
                          const checked = selectedKeys.includes(key);
                          return (
                            <li key={key}>
                              <label
                                className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${
                                  isSubmitting
                                    ? "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                                    : checked
                                      ? "border-orange-primary bg-orange-50"
                                      : "border-gray-200 hover:border-orange-200"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={isSubmitting}
                                  onChange={() => toggle(key)}
                                />
                                <span className="text-sm font-medium text-gray-900">{title}</span>
                              </label>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || selectedKeys.length === 0}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-orange-primary hover:bg-orange-600 rounded-lg disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSubmitting
              ? "Copying…"
              : selectedKeys.length
                ? `Copy to ${selectedKeys.length} slot${selectedKeys.length === 1 ? "" : "s"}`
                : "Select slots"}
          </button>
        </div>
      </div>
    </div>
  );
}
